/**
 * Flat spot illustrations for the applicant journey (guide screens, the
 * "submitted" wait screen). Inline SVG for the same reason as StatusGlyph: no
 * asset requests, identical across platforms.
 *
 * Colours come from CSS classes (`.ill-*` in theme.css), never from `fill`
 * attributes: presentation attributes do not resolve `var(--token)`, and the
 * classes keep the drawings on the theme's tokens if the brand colour changes.
 * Flat on purpose — the theme dropped glows and gradients on 2026-09-17.
 */
import type { ReactNode } from 'react';

type IllustrationKind = 'id-card' | 'selfie' | 'submitted';

export function Illustration({ kind }: { kind: IllustrationKind }) {
  return (
    <div className="illustration" aria-hidden="true">
      <svg viewBox="0 0 200 150" fill="none">
        <circle className="ill-bg" cx="100" cy="75" r="68" />
        {DRAWINGS[kind]}
      </svg>
    </div>
  );
}

const CHECK_BADGE = (
  <g>
    <circle className="ill-accent" cx="150" cy="40" r="17" />
    <path className="ill-check" d="M142 40.5l5.5 5.5L158 35.5" />
  </g>
);

const DRAWINGS: Record<IllustrationKind, ReactNode> = {
  'id-card': (
    <g>
      <rect className="ill-paper" x="40" y="44" width="120" height="76" rx="10" />
      <rect className="ill-tint" x="52" y="58" width="34" height="42" rx="6" />
      <circle className="ill-accent" cx="69" cy="74" r="7" />
      <path className="ill-accent" d="M58 96c1.5-7.5 6-11 11-11s9.5 3.5 11 11z" />
      <rect className="ill-line" x="96" y="62" width="50" height="6" rx="3" />
      <rect className="ill-line" x="96" y="76" width="38" height="6" rx="3" />
      <rect className="ill-line" x="96" y="90" width="44" height="6" rx="3" />
      {CHECK_BADGE}
    </g>
  ),
  selfie: (
    <g>
      <rect className="ill-paper" x="70" y="22" width="60" height="108" rx="12" />
      <rect className="ill-tint" x="77" y="34" width="46" height="84" rx="6" />
      <ellipse className="ill-oval" cx="100" cy="70" rx="15" ry="19" />
      <circle className="ill-ink" cx="94.5" cy="67" r="1.8" />
      <circle className="ill-ink" cx="105.5" cy="67" r="1.8" />
      <path className="ill-smile" d="M94.5 75.5c3 3 8 3 11 0" />
      {CHECK_BADGE}
    </g>
  ),
  submitted: (
    <g>
      <rect className="ill-paper" x="60" y="28" width="80" height="98" rx="10" />
      <rect className="ill-line" x="74" y="46" width="52" height="6" rx="3" />
      <rect className="ill-line" x="74" y="60" width="40" height="6" rx="3" />
      <rect className="ill-line" x="74" y="74" width="46" height="6" rx="3" />
      <rect className="ill-tint" x="74" y="90" width="52" height="22" rx="5" />
      {CHECK_BADGE}
    </g>
  ),
};
